import { useMemo, useState } from "react";
import { ScatterChart, Scatter, XAxis, YAxis, ZAxis, Tooltip, ReferenceLine, ResponsiveContainer, LineChart, Line, Cell } from "recharts";
import { Search, ArrowUpDown } from "lucide-react";
import { computeTlRows, computeAgentRows, trendSeries, sortRows, median, fmtN, fmtPct, fmtInt, kpisOf, METRICS, C, SITE_COLORS } from "./icUtils";

const card = "mk-card";
const th = "font-mono-hud cursor-pointer select-none whitespace-nowrap px-3 py-2.5 text-[9px] uppercase tracking-[0.12em] text-thdim hover:text-thcyan";
const fmtFor = (m) => (m.pct ? fmtPct : m.int ? fmtInt : (v) => fmtN(v, m.d ?? 1));

const SortTh = ({ label, k, sort, setSort, right = true }) => (
  <th className={`${th} ${right ? "text-right" : "text-left"}`} onClick={() => setSort((s) => ({ k, dir: s.k === k ? -s.dir : 1 }))}>
    <span className="inline-flex items-center gap-1">{label}{sort.k === k && <ArrowUpDown className="h-2.5 w-2.5 text-thcyan" />}</span>
  </th>
);

export default function TlAgentsView({ data, slices, site, onOpenAgent, initialTl }) {
  const [tlSort, setTlSort] = useState({ k: "pmt", dir: 1 });
  const [agSort, setAgSort] = useState({ k: "composite", dir: 1 });
  const [selectedTl, setSelectedTl] = useState(initialTl || "");
  const [search, setSearch] = useState("");
  const [trendMode, setTrendMode] = useState("w");
  const [trendLevel, setTrendLevel] = useState("overall");
  const [trendEntity, setTrendEntity] = useState("");

  const tlRows = useMemo(() => sortRows(computeTlRows(data, slices), tlSort.k, tlSort.dir), [data, slices, tlSort]);
  const agentRowsAll = useMemo(() => computeAgentRows(data, slices), [data, slices]);
  const agentRows = useMemo(() => {
    let rows = agentRowsAll;
    if (selectedTl) rows = rows.filter((r) => r.tl === selectedTl);
    if (search) { const q = search.toLowerCase(); rows = rows.filter((r) => r.name.toLowerCase().includes(q) || r.c.includes(q) || r.tl.toLowerCase().includes(q)); }
    return sortRows(rows, agSort.k, agSort.dir);
  }, [agentRowsAll, selectedTl, search, agSort]);

  const teamRow = useMemo(() => {
    const set = selectedTl ? new Set(agentRowsAll.filter((r) => r.tl === selectedTl).map((r) => r.c)) : null;
    const scoped = set ? { voc: slices.voc.filter((r) => set.has(r.c)), chat: slices.chat.filter((r) => set.has(r.c)), pmt: slices.pmt.filter((r) => set.has(r.c)), qsl: slices.qsl.filter((r) => set.has(r.c)) } : slices;
    return kpisOf(scoped);
  }, [slices, selectedTl, agentRowsAll]);

  const trend = useMemo(() => {
    let ciscos = null;
    if (trendLevel === "tl" && trendEntity) ciscos = new Set(Object.keys(data.agents).filter((c) => data.agents[c].tl === trendEntity));
    if (trendLevel === "agent" && trendEntity) ciscos = new Set([trendEntity]);
    return trendSeries(data, { mode: trendMode, ciscos, site: trendLevel === "overall" ? site : "All" });
  }, [data, trendMode, trendLevel, trendEntity, site]);

  const tlNames = useMemo(() => [...new Set(Object.values(data.agents).map((a) => a.tl).filter((t) => t && t !== "-"))].sort(), [data]);
  const agentOptions = useMemo(() => Object.entries(data.agents).filter(([, a]) => a.name).sort((a, b) => a[1].name.localeCompare(b[1].name)), [data]);

  const quadrant = useMemo(() => {
    return agentRowsAll
      .filter((r) => r.surveys >= 3 && (r.ahtWA != null || r.ahtLC != null))
      .map((r) => ({ ...r, aht: (r.ahtWA != null && r.ahtLC != null) ? (r.ahtWA + r.ahtLC) / 2 : (r.ahtWA ?? r.ahtLC) }));
  }, [agentRowsAll]);
  const medAht = median(quadrant.map((r) => r.aht));
  const medVoc = median(quadrant.map((r) => r.voc).filter((v) => v != null));

  return (
    <div className="space-y-8">
      <div className={card} data-testid="ic-tl-table">
        <p className="font-mono-hud px-5 pt-4 text-[10px] uppercase tracking-[0.25em] text-thdim">TL League Table · click a TL to open their team</p>
        <div className="mt-2 overflow-x-auto">
          <table className="w-full text-left">
            <thead><tr className="border-b border-thborder/10">
              <SortTh label="Team Leader" k="tl" sort={tlSort} setSort={setTlSort} right={false} />
              <SortTh label="Agents" k="agents" sort={tlSort} setSort={setTlSort} />
              <SortTh label="Evals" k="evals" sort={tlSort} setSort={setTlSort} />
              <SortTh label="PMT %" k="pmt" sort={tlSort} setSort={setTlSort} />
              <SortTh label="Criticals" k="crit" sort={tlSort} setSort={setTlSort} />
              <SortTh label="Crit %" k="critPct" sort={tlSort} setSort={setTlSort} />
              <SortTh label="VOC %" k="voc" sort={tlSort} setSort={setTlSort} />
              <SortTh label="AHT WA" k="ahtWA" sort={tlSort} setSort={setTlSort} />
              <SortTh label="AHT LC" k="ahtLC" sort={tlSort} setSort={setTlSort} />
            </tr></thead>
            <tbody>
              {tlRows.map((r) => (
                <tr key={r.tl} data-testid={`ic-tl-row-${r.tl.replace(/\s+/g, "-")}`} onClick={() => setSelectedTl(r.tl === selectedTl ? "" : r.tl)}
                  className={`cursor-pointer border-b border-thborder/5 transition-colors hover:bg-thhover/5 ${selectedTl === r.tl ? "bg-thcyan/5" : ""}`}>
                  <td className="px-3 py-2.5 text-xs font-medium text-thstrong">{r.tl}</td>
                  <td className="px-3 py-2.5 text-right font-mono-hud text-xs text-thbody">{r.agents}</td>
                  <td className="px-3 py-2.5 text-right font-mono-hud text-xs text-thbody">{r.evals || "—"}</td>
                  <td className={`px-3 py-2.5 text-right font-mono-hud text-xs font-semibold ${r.pmt == null ? "text-thdim" : r.pmt >= 80 ? "text-[#10B981]" : "text-[#F43F5E]"}`}>{fmtPct(r.pmt)}</td>
                  <td className="px-3 py-2.5 text-right font-mono-hud text-xs text-thbody">{r.evals ? r.crit : "—"}</td>
                  <td className="px-3 py-2.5 text-right font-mono-hud text-xs text-thbody">{fmtPct(r.critPct)}</td>
                  <td className="px-3 py-2.5 text-right font-mono-hud text-xs text-thbody">{fmtPct(r.voc)}</td>
                  <td className="px-3 py-2.5 text-right font-mono-hud text-xs text-thbody">{fmtN(r.ahtWA, 0)}</td>
                  <td className="px-3 py-2.5 text-right font-mono-hud text-xs text-thbody">{fmtN(r.ahtLC, 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className={card} data-testid="ic-agent-table">
        <div className="flex flex-wrap items-center justify-between gap-3 px-5 pt-4">
          <p className="font-mono-hud text-[10px] uppercase tracking-[0.25em] text-thdim">Agent Scorecards · click a row for the drawer</p>
          <div className="flex items-center gap-2">
            <select data-testid="ic-agent-tl-select" value={selectedTl} onChange={(e) => setSelectedTl(e.target.value)}
              className="rounded-md border border-thborder/10 bg-thinput px-2.5 py-1.5 text-xs text-thstrong focus:border-thcyan focus:outline-none">
              <option value="">All TLs</option>
              {tlNames.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <div className="relative">
              <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-thdim" />
              <input data-testid="ic-agent-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search agent, ID, TL..."
                className="rounded-md border border-thborder/10 bg-thinput py-1.5 pl-8 pr-3 text-xs text-thstrong focus:border-thcyan focus:outline-none" />
            </div>
          </div>
        </div>
        <div className="mt-2 overflow-x-auto">
          <table className="w-full text-left">
            <thead><tr className="border-b border-thborder/10">
              <SortTh label="Cisco" k="c" sort={agSort} setSort={setAgSort} right={false} />
              <SortTh label="Agent" k="name" sort={agSort} setSort={setAgSort} right={false} />
              <SortTh label="TL" k="tl" sort={agSort} setSort={setAgSort} right={false} />
              <SortTh label="Site" k="site" sort={agSort} setSort={setAgSort} right={false} />
              <SortTh label="VOC %" k="voc" sort={agSort} setSort={setAgSort} />
              <SortTh label="Srv" k="surveys" sort={agSort} setSort={setAgSort} />
              <SortTh label="AHT WA" k="ahtWA" sort={agSort} setSort={setAgSort} />
              <SortTh label="AHT LC" k="ahtLC" sort={agSort} setSort={setAgSort} />
              <SortTh label="FRT WA" k="frtWA" sort={agSort} setSort={setAgSort} />
              <SortTh label="FRT LC" k="frtLC" sort={agSort} setSort={setAgSort} />
              <SortTh label="PMT" k="pmt" sort={agSort} setSort={setAgSort} />
              <SortTh label="Crit" k="crit" sort={agSort} setSort={setAgSort} />
              <SortTh label="QSL" k="qsl" sort={agSort} setSort={setAgSort} />
              <SortTh label="Fatal" k="fatals" sort={agSort} setSort={setAgSort} />
              <SortTh label="Adh" k="adh" sort={agSort} setSort={setAgSort} />
              <SortTh label="Comp" k="composite" sort={agSort} setSort={setAgSort} />
            </tr></thead>
            <tbody>
              <tr data-testid="ic-team-row" className="border-b border-thborder/10 bg-thcyan/5">
                <td className="px-3 py-2.5 font-mono-hud text-xs text-thcyan">—</td>
                <td className="px-3 py-2.5 text-xs font-semibold text-thcyan">{selectedTl ? `TEAM — ${selectedTl}` : "TEAM — all agents"}</td>
                <td className="px-3 py-2.5 text-xs text-thdim">—</td><td className="px-3 py-2.5 text-xs text-thdim">—</td>
                <td className="px-3 py-2.5 text-right font-mono-hud text-xs font-semibold text-thcyan">{fmtPct(teamRow.voc)}</td>
                <td className="px-3 py-2.5 text-right font-mono-hud text-xs text-thcyan">{fmtInt(teamRow.surveys)}</td>
                <td className="px-3 py-2.5 text-right font-mono-hud text-xs text-thcyan">{fmtN(teamRow.ahtWA, 0)}</td>
                <td className="px-3 py-2.5 text-right font-mono-hud text-xs text-thcyan">{fmtN(teamRow.ahtLC, 0)}</td>
                <td className="px-3 py-2.5 text-right font-mono-hud text-xs text-thcyan">{fmtN(teamRow.frtWA, 2)}</td>
                <td className="px-3 py-2.5 text-right font-mono-hud text-xs text-thcyan">{fmtN(teamRow.frtLC, 2)}</td>
                <td className="px-3 py-2.5 text-right font-mono-hud text-xs font-semibold text-thcyan">{fmtPct(teamRow.pmt)}</td>
                <td className="px-3 py-2.5 text-right font-mono-hud text-xs text-thcyan">{teamRow.crit}</td>
                <td className="px-3 py-2.5 text-right font-mono-hud text-xs text-thcyan">{fmtPct(teamRow.qsl)}</td>
                <td className="px-3 py-2.5 text-right font-mono-hud text-xs text-thcyan">{teamRow.fatals}</td>
                <td className="px-3 py-2.5 text-right font-mono-hud text-xs text-thdim">—</td>
                <td className="px-3 py-2.5 text-right font-mono-hud text-xs text-thdim">—</td>
              </tr>
              {agentRows.map((r) => (
                <tr key={r.c} data-testid={`ic-agent-row-${r.c}`} onClick={() => onOpenAgent(r.c)} className="cursor-pointer border-b border-thborder/5 hover:bg-thhover/5">
                  <td className="px-3 py-2 font-mono-hud text-xs text-thdim">{r.c}</td>
                  <td className="max-w-[180px] truncate px-3 py-2 text-xs font-medium text-thstrong">{r.name}</td>
                  <td className="max-w-[130px] truncate px-3 py-2 text-xs text-thmute">{r.tl}</td>
                  <td className="px-3 py-2 text-xs"><span style={{ color: SITE_COLORS[r.site] }}>{r.site}</span></td>
                  <td className="px-3 py-2 text-right font-mono-hud text-xs text-thbody">{fmtPct(r.voc)}</td>
                  <td className="px-3 py-2 text-right font-mono-hud text-xs text-thbody">{r.surveys || "—"}</td>
                  <td className="px-3 py-2 text-right font-mono-hud text-xs text-thbody">{fmtN(r.ahtWA, 0)}</td>
                  <td className="px-3 py-2 text-right font-mono-hud text-xs text-thbody">{fmtN(r.ahtLC, 0)}</td>
                  <td className="px-3 py-2 text-right font-mono-hud text-xs text-thbody">{fmtN(r.frtWA, 2)}</td>
                  <td className="px-3 py-2 text-right font-mono-hud text-xs text-thbody">{fmtN(r.frtLC, 2)}</td>
                  <td className={`px-3 py-2 text-right font-mono-hud text-xs ${r.pmt == null ? "text-thdim" : r.pmt >= 80 ? "text-[#10B981]" : "text-[#F43F5E]"}`}>{fmtPct(r.pmt)}</td>
                  <td className="px-3 py-2 text-right font-mono-hud text-xs text-thbody">{r.evals ? r.crit : "—"}</td>
                  <td className="px-3 py-2 text-right font-mono-hud text-xs text-thbody">{fmtPct(r.qsl)}</td>
                  <td className="px-3 py-2 text-right font-mono-hud text-xs text-thbody">{r.audits ? r.fatals : "—"}</td>
                  <td className="px-3 py-2 text-right font-mono-hud text-xs text-thbody">{fmtPct(r.adh)}</td>
                  <td className="px-3 py-2 text-right font-mono-hud text-xs font-semibold text-thbody">{fmtPct(r.composite)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="px-5 py-3 text-xs text-thdim">{agentRows.length} agents · blanks always sort last · composite = simple average of available VOC / PMT / QSL / Adherence</p>
      </div>

      <div className={`${card} p-5`} data-testid="ic-trend-explorer">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <p className="font-mono-hud text-[10px] uppercase tracking-[0.25em] text-thdim">Trend Explorer · full history (date filter ignored)</p>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex gap-1 rounded-lg bg-thhover/5 p-1">
              {[["w", "Weekly"], ["m", "Monthly"]].map(([m, l]) => (
                <button key={m} data-testid={`ic-trend-mode-${l.toLowerCase()}`} onClick={() => setTrendMode(m)}
                  className={`rounded-md px-3 py-1 text-xs font-medium ${trendMode === m ? "bg-thcyan/15 text-thcyan" : "text-thmute hover:text-thbody"}`}>{l}</button>
              ))}
            </div>
            <select data-testid="ic-trend-level" value={trendLevel} onChange={(e) => { setTrendLevel(e.target.value); setTrendEntity(""); }}
              className="rounded-md border border-thborder/10 bg-thinput px-2.5 py-1.5 text-xs text-thstrong focus:border-thcyan focus:outline-none">
              <option value="overall">Overall</option><option value="tl">By TL</option><option value="agent">By Agent</option>
            </select>
            {trendLevel === "tl" && (
              <select data-testid="ic-trend-entity" value={trendEntity} onChange={(e) => setTrendEntity(e.target.value)}
                className="rounded-md border border-thborder/10 bg-thinput px-2.5 py-1.5 text-xs text-thstrong focus:border-thcyan focus:outline-none">
                <option value="">Choose TL…</option>
                {tlNames.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            )}
            {trendLevel === "agent" && (
              <select data-testid="ic-trend-entity" value={trendEntity} onChange={(e) => setTrendEntity(e.target.value)}
                className="max-w-[220px] rounded-md border border-thborder/10 bg-thinput px-2.5 py-1.5 text-xs text-thstrong focus:border-thcyan focus:outline-none">
                <option value="">Choose agent…</option>
                {agentOptions.map(([c, a]) => <option key={c} value={c}>{a.name} ({c})</option>)}
              </select>
            )}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead><tr className="border-b border-thborder/10">
              <th className={`${th} text-left`}>Metric</th>
              {trend.map((p) => <th key={p.key} className={`${th} text-right`}>{p.label}</th>)}
              <th className={`${th} text-right`}>Δ last</th>
            </tr></thead>
            <tbody>
              {METRICS.map((m) => {
                const vals = trend.map((p) => p[m.k]);
                const last = vals[vals.length - 1], prev = vals[vals.length - 2];
                const delta = last != null && prev != null ? last - prev : null;
                const good = delta == null ? null : m.lower ? delta <= 0 : delta >= 0;
                return (
                  <tr key={m.k} className="border-b border-thborder/5">
                    <td className="px-3 py-2 text-xs text-thbody">{m.label}</td>
                    {vals.map((v, i) => <td key={i} className="px-3 py-2 text-right font-mono-hud text-xs text-thbody">{fmtFor(m)(v)}</td>)}
                    <td className={`px-3 py-2 text-right font-mono-hud text-xs font-semibold ${good == null ? "text-thdim" : good ? "text-[#10B981]" : "text-[#F43F5E]"}`}>
                      {delta == null ? "—" : `${delta > 0 ? "+" : ""}${fmtN(delta, m.int ? 0 : 1)}`}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {METRICS.map((m) => {
            const pts = trend.map((p) => ({ label: p.label, v: p[m.k] })).filter((p) => p.v != null);
            if (pts.length < 2) return null;
            return (
              <div key={m.k} className="rounded-lg border border-thborder/10 bg-thhover/5 p-3">
                <p className="mb-1 text-xs font-medium text-thbody">{m.label}</p>
                <div className="h-28">
                  <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={100} initialDimension={{ width: 1, height: 1 }}>
                    <LineChart data={pts} margin={{ top: 4, right: 8, bottom: 0, left: -18 }}>
                      <XAxis dataKey="label" tick={{ fill: "#7a8fb5", fontSize: 9, fontFamily: "JetBrains Mono" }} interval="preserveStartEnd" />
                      <YAxis tick={{ fill: "#7a8fb5", fontSize: 9, fontFamily: "JetBrains Mono" }} domain={["auto", "auto"]} />
                      <Tooltip contentStyle={{ background: "rgb(var(--th-solid))", border: "1px solid rgb(var(--th-border) / 0.14)", borderRadius: 10, fontSize: 11, color: "rgb(var(--th-strong))" }} labelStyle={{ color: "rgb(var(--th-mute))" }} />
                      {m.target != null && <ReferenceLine y={m.target} stroke={C.yellow} strokeDasharray="5 4" strokeOpacity={0.7} />}
                      <Line type="monotone" dataKey="v" stroke={m.lower ? C.purple : C.green} strokeWidth={2} dot={{ r: 2 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className={`${card} p-5`} data-testid="ic-quadrant">
        <p className="font-mono-hud text-[10px] uppercase tracking-[0.25em] text-thdim">AHT vs VOC Quadrant · agents with ≥3 surveys · click a point for the drawer</p>
        <p className="mt-1 mb-2 text-xs text-thdim">Crosshairs at the median. Ideal = low AHT, high VOC (top-left).</p>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={100} initialDimension={{ width: 1, height: 1 }}>
            <ScatterChart margin={{ top: 10, right: 20, bottom: 10, left: -8 }}>
              <XAxis dataKey="aht" name="AHT (s)" type="number" tick={{ fill: "#7a8fb5", fontSize: 10, fontFamily: "JetBrains Mono" }} domain={["auto", "auto"]}
                label={{ value: "AHT (sec)", position: "insideBottom", offset: -6, fill: "#64748b", fontSize: 10 }} />
              <YAxis dataKey="voc" name="VOC %" type="number" tick={{ fill: "#7a8fb5", fontSize: 10, fontFamily: "JetBrains Mono" }} domain={["auto", "auto"]} />
              <ZAxis dataKey="surveys" range={[35, 220]} name="Surveys" />
              {medAht != null && <ReferenceLine x={medAht} stroke="#7a8fb5" strokeDasharray="4 4" />}
              {medVoc != null && <ReferenceLine y={medVoc} stroke="#7a8fb5" strokeDasharray="4 4" />}
              <Tooltip cursor={{ strokeDasharray: "3 3" }} content={({ payload }) => {
                const p = payload?.[0]?.payload;
                if (!p) return null;
                return (
                  <div className="rounded-xl border border-thborder/14 bg-thsolid px-3 py-2 font-mono-hud text-xs text-thstrong shadow-card">
                    <p className="font-semibold">{p.name} <span className="font-mono-hud text-[10px] text-slate-400">{p.c}</span></p>
                    <p className="text-slate-300">VOC {fmtPct(p.voc)} · AHT {fmtN(p.aht, 0)}s · {p.surveys} surveys · {p.site}</p>
                  </div>
                );
              }} />
              <Scatter data={quadrant} onClick={(p) => p && onOpenAgent(p.c)}>
                {quadrant.map((r) => <Cell key={r.c} fill={SITE_COLORS[r.site]} fillOpacity={0.85} className="cursor-pointer" />)}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-2 flex gap-4">
          {Object.entries(SITE_COLORS).map(([s, c]) => (
            <span key={s} className="flex items-center gap-1.5 text-xs text-thmute"><span className="h-2.5 w-2.5 rounded-full" style={{ background: c }} />{s}</span>
          ))}
        </div>
      </div>
    </div>
  );
}
